import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Business } from '@/types/business';
import { regenerateInviteLink } from '@/utils/inviteService';
import toast from 'react-hot-toast';
import {
  Building2,
  RefreshCw,
  Search,
  Copy,
  Link2,
  Eye,
  Users,
  Mail,
  Phone,
  MapPin,
  Globe,
  X,
  Pause,
  Play,
  Filter
} from 'lucide-react';

interface UserData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
  createdAt?: any;
}

const SuperAdminBusinessesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState<Business[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Modal de detalle
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [businessUsers, setBusinessUsers] = useState<UserData[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);

  // Acciones
  const [updatingBusiness, setUpdatingBusiness] = useState<string | null>(null);

  const loadBusinesses = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'businesses'));
      const items = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as Business[];
      // Ordenar por fecha de creación (más recientes primero)
      items.sort((a, b) => {
        const dateA = (a.createdAt as any)?.toDate?.() || new Date((a.createdAt as any) || 0);
        const dateB = (b.createdAt as any)?.toDate?.() || new Date((b.createdAt as any) || 0);
        return dateB.getTime() - dateA.getTime();
      });
      setBusinesses(items);
      setFilteredBusinesses(items);
    } catch (e) {
      console.error('Error cargando negocios:', e);
      toast.error('Error cargando negocios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBusinesses();
  }, []);

  // Filtrar negocios
  useEffect(() => {
    let filtered = [...businesses];

    // Filtro de búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(b =>
        b.name?.toLowerCase().includes(term) ||
        b.adminEmail?.toLowerCase().includes(term) ||
        b.slug?.toLowerCase().includes(term)
      );
    }

    // Filtro de plan
    if (filterPlan !== 'all') {
      filtered = filtered.filter(b => b.subscription?.plan === filterPlan);
    }

    // Filtro de estado
    if (filterStatus !== 'all') {
      filtered = filtered.filter(b => b.subscription?.status === filterStatus);
    }

    setFilteredBusinesses(filtered);
  }, [searchTerm, filterPlan, filterStatus, businesses]);

  const handleRegenerateInvite = async (businessId: string) => {
    try {
      const newLink = await regenerateInviteLink(businessId);
      toast.success('Enlace de invitación regenerado');
      await setDoc(doc(db, 'businesses', businessId), { inviteLink: newLink, updatedAt: new Date() } as any, { merge: true });
      await loadBusinesses();
    } catch (e: any) {
      console.error('Error regenerando enlace:', e);
      toast.error(e?.message || 'Error regenerando enlace');
    }
  };

  const handleCopyInviteLink = (link: string) => {
    navigator.clipboard.writeText(link);
    toast.success('Enlace copiado al portapapeles');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadBusinesses();
    setRefreshing(false);
    toast.success('Lista actualizada');
  };

  const handleViewDetails = async (business: Business) => {
    setSelectedBusiness(business);
    setShowDetailModal(true);
    setLoadingUsers(true);
    setShowAllUsers(false); // Reset expansion state when opening a new business

    try {
      // Cargar usuarios del negocio
      const usersQuery = query(
        collection(db, 'users'),
        where('businessId', '==', business.id)
      );
      const usersSnap = await getDocs(usersQuery);
      const users = usersSnap.docs.map(d => ({
        id: d.id,
        ...d.data()
      })) as UserData[];
      setBusinessUsers(users);
    } catch (e) {
      console.error('Error cargando usuarios:', e);
      toast.error('Error cargando usuarios del negocio');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChangePlan = async (businessId: string, newPlan: string) => {
    if (!confirm(`¿Cambiar el plan a "${newPlan}"?`)) return;

    setUpdatingBusiness(businessId);
    try {
      await updateDoc(doc(db, 'businesses', businessId), {
        'subscription.plan': newPlan,
        updatedAt: new Date()
      });
      toast.success(`Plan cambiado a ${newPlan}`);
      await loadBusinesses();

      // Actualizar el negocio seleccionado si está abierto
      if (selectedBusiness?.id === businessId) {
        setSelectedBusiness({
          ...selectedBusiness,
          subscription: { ...selectedBusiness.subscription, plan: newPlan }
        } as Business);
      }
    } catch (e: any) {
      console.error('Error cambiando plan:', e);
      toast.error('Error cambiando el plan');
    } finally {
      setUpdatingBusiness(null);
    }
  };

  const handleToggleStatus = async (businessId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    const action = newStatus === 'suspended' ? 'suspender' : 'reactivar';

    if (!confirm(`¿Estás seguro de ${action} este negocio?`)) return;

    setUpdatingBusiness(businessId);
    try {
      await updateDoc(doc(db, 'businesses', businessId), {
        'subscription.status': newStatus,
        updatedAt: new Date()
      });
      toast.success(`Negocio ${newStatus === 'active' ? 'reactivado' : 'suspendido'}`);
      await loadBusinesses();

      // Actualizar el negocio seleccionado si está abierto
      if (selectedBusiness?.id === businessId) {
        setSelectedBusiness({
          ...selectedBusiness,
          subscription: { ...selectedBusiness.subscription, status: newStatus }
        } as Business);
      }
    } catch (e: any) {
      console.error('Error cambiando estado:', e);
      toast.error('Error cambiando el estado');
    } finally {
      setUpdatingBusiness(null);
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    const d = date?.toDate?.() || new Date(date);
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getPlanBadgeClass = (plan: string | undefined) => {
    switch (plan) {
      case 'pro':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      case 'basic':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusBadgeClass = (status: string | undefined) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      case 'suspended':
        return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Building2 className="w-7 h-7 text-amber-500" />
            Gestión de Negocios
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {filteredBusinesses.length} de {businesses.length} negocios
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Búsqueda */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, email o slug..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          {/* Filtro de Plan */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
              className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
            >
              <option value="all">Todos los planes</option>
              <option value="free">Gratuito</option>
              <option value="basic">Básico</option>
              <option value="pro">Profesional</option>
            </select>
          </div>

          {/* Filtro de Estado */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="suspended">Suspendidos</option>
          </select>
        </div>
      </div>

      {/* Tabla de Negocios */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Negocio
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Administrador
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Plan
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Estado
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Creado
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredBusinesses.map(b => (
                <tr key={b.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {b.logo ? (
                        <img src={b.logo} alt={b.name} className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{b.name}</p>
                        <p className="text-xs text-gray-500">/{b.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-gray-900 dark:text-white">{b.adminEmail}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getPlanBadgeClass(b.subscription?.plan)}`}>
                      {b.subscription?.plan || 'free'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusBadgeClass(b.subscription?.status)}`}>
                      {b.subscription?.status || 'active'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                    {formatDate(b.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleViewDetails(b)}
                        className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
                        title="Ver detalles"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCopyInviteLink(b.inviteLink || '')}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Copiar enlace"
                        disabled={!b.inviteLink}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRegenerateInvite(b.id)}
                        className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                        title="Regenerar enlace"
                      >
                        <Link2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(b.id, b.subscription?.status || 'active')}
                        disabled={updatingBusiness === b.id}
                        className={`p-2 rounded-lg transition-colors ${b.subscription?.status === 'active'
                            ? 'text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                            : 'text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                          }`}
                        title={b.subscription?.status === 'active' ? 'Suspender' : 'Reactivar'}
                      >
                        {updatingBusiness === b.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : b.subscription?.status === 'active' ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredBusinesses.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-2 opacity-20" />
            <p>No se encontraron negocios</p>
          </div>
        )}
      </div>

      {/* Modal de Detalle */}
      {showDetailModal && selectedBusiness && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header del modal */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {selectedBusiness.logo ? (
                  <img src={selectedBusiness.logo} alt={selectedBusiness.name} className="w-12 h-12 rounded-xl object-cover" />
                ) : (
                  <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedBusiness.name}</h2>
                  <p className="text-sm text-gray-500">/{selectedBusiness.slug}</p>
                </div>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Contenido del modal */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Info general */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{selectedBusiness.adminEmail}</span>
                  </div>
                  {selectedBusiness.phone && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{selectedBusiness.phone}</span>
                    </div>
                  )}
                  {selectedBusiness.address && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span>{selectedBusiness.address}</span>
                    </div>
                  )}
                  {(selectedBusiness as any).website && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <Globe className="w-4 h-4 text-gray-400" />
                      <a href={(selectedBusiness as any).website} target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline">
                        {(selectedBusiness as any).website}
                      </a>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <span className="text-sm text-gray-500">Plan actual</span>
                    <select
                      value={selectedBusiness.subscription?.plan || 'free'}
                      onChange={(e) => handleChangePlan(selectedBusiness.id, e.target.value)}
                      disabled={updatingBusiness === selectedBusiness.id}
                      className="px-3 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                    >
                      <option value="free">Gratuito</option>
                      <option value="basic">Básico</option>
                      <option value="pro">Profesional</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <span className="text-sm text-gray-500">Estado</span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusBadgeClass(selectedBusiness.subscription?.status)}`}>
                      {selectedBusiness.subscription?.status || 'active'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <span className="text-sm text-gray-500">Stripe Connect</span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${(selectedBusiness as any).stripeConnectStatus === 'active'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600'
                      }`}>
                      {(selectedBusiness as any).stripeConnectStatus || 'No conectado'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Enlace de invitación */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Enlace de invitación</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCopyInviteLink(selectedBusiness.inviteLink || '')}
                      className="text-xs px-3 py-1 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                    >
                      Copiar
                    </button>
                    <button
                      onClick={() => handleRegenerateInvite(selectedBusiness.id)}
                      className="text-xs px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Regenerar
                    </button>
                  </div>
                </div>
                <code className="block p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs text-gray-600 dark:text-gray-400 break-all">
                  {selectedBusiness.inviteLink || 'No disponible'}
                </code>
              </div>

              {/* Usuarios del negocio */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-500" />
                  Usuarios ({businessUsers.length})
                </h3>

                {loadingUsers ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
                  </div>
                ) : businessUsers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-20" />
                    <p>No hay usuarios registrados</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Usuario</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Rol</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {(showAllUsers ? businessUsers : businessUsers.slice(0, 10)).map(user => (
                          <tr key={user.id}>
                            <td className="px-4 py-2">
                              <p className="font-medium text-gray-900 dark:text-white">
                                {user.firstName} {user.lastName}
                              </p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                            </td>
                            <td className="px-4 py-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${user.role === 'admin'
                                  ? 'bg-purple-100 text-purple-600'
                                  : user.role === 'instructor'
                                    ? 'bg-blue-100 text-blue-600'
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                {user.role}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${user.status === 'active'
                                  ? 'bg-green-100 text-green-600'
                                  : 'bg-red-100 text-red-600'
                                }`}>
                                {user.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {businessUsers.length > 10 && (
                      <button
                        onClick={() => setShowAllUsers(!showAllUsers)}
                        className="w-full p-2 text-center text-xs text-amber-600 dark:text-amber-400 bg-gray-50 dark:bg-gray-700/50 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors cursor-pointer font-medium"
                      >
                        {showAllUsers
                          ? 'Mostrar menos'
                          : `Ver todos (${businessUsers.length - 10} usuarios más)`
                        }
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer del modal */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
              <button
                onClick={() => handleToggleStatus(selectedBusiness.id, selectedBusiness.subscription?.status || 'active')}
                disabled={updatingBusiness === selectedBusiness.id}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${selectedBusiness.subscription?.status === 'active'
                    ? 'bg-red-100 text-red-600 hover:bg-red-200'
                    : 'bg-green-100 text-green-600 hover:bg-green-200'
                  }`}
              >
                {updatingBusiness === selectedBusiness.id ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : selectedBusiness.subscription?.status === 'active' ? (
                  <>
                    <Pause className="w-4 h-4" />
                    Suspender Negocio
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Reactivar Negocio
                  </>
                )}
              </button>
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminBusinessesPage;
